import { useState, useEffect, useMemo } from 'react';
import { useService } from '@aesop-fables/containr-react';
import { PricebookBundle, PricebookBundleMapping } from '../models/PricebookData';
import { PricebookBundleApi } from '../api/apis/PricebookBundleApi';
import { ApiKeys } from '../api/apis/ApiKeys';
import { isFreemiumPricebook } from '../helpers/pricebookUtils';

export const useAddOnPricebookLookup = (
  pricebooks: PricebookBundle[] | undefined,
): Map<number, PricebookBundle> => {
  const bundleApi = useService<PricebookBundleApi>(ApiKeys.PricebookBundle);
  const [mappings, setMappings] = useState<PricebookBundleMapping[]>([]);

  const freemiumPricebookIdsKey = (pricebooks ?? [])
    .filter(pb => isFreemiumPricebook(pb.pricebook))
    .map(pb => pb.pricebook.id)
    .sort((a, b) => a - b)
    .join(',');

  useEffect(() => {
    const freemiumPricebookIds = freemiumPricebookIdsKey
      ? freemiumPricebookIdsKey.split(',').map(Number)
      : [];

    if (freemiumPricebookIds.length === 0) {
      setMappings([]);
      return;
    }

    let isCancelled = false;

    Promise.all(freemiumPricebookIds.map(id => bundleApi.getByPrimaryPricebook(id)))
      .then(responses => {
        if (!isCancelled) {
          setMappings(responses.flatMap(response => response.data));
        }
      })
      .catch(err => {
        if (!isCancelled) {
          console.error('Error loading pricebook bundle mappings', err);
          setMappings([]);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [freemiumPricebookIdsKey, bundleApi]);

  return useMemo(() => {
    const pricebooksById = new Map((pricebooks ?? []).map(pb => [pb.pricebook.id, pb] as const));
    const addOnsByBaseId = new Map<number, PricebookBundle>();
    for (const mapping of mappings) {
      const addOnPricebook = pricebooksById.get(mapping.addOnPricebookId);
      if (addOnPricebook) {
        addOnsByBaseId.set(mapping.primaryPricebookId, addOnPricebook);
      }
    }
    return addOnsByBaseId;
  }, [mappings, pricebooks]);
};
